import type { TFunction } from 'i18next';

import { type MaasGroupSpec, MaasRunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  type GpuValidationOptions,
  findGpuUsageViolationFromNodeFieldsLatest,
  formatGpuUsageValidationError,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/gpuResourceValidation';
import { parseNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import type { EmbeddedNodeSpec } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import {
  hasDuplicateWorkerGroupNames,
  hasWorkerGroupReservedRoleName,
  validateNodeGroupResource,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeGroupValidation';

function validateWorkerGroups(workers: MaasGroupSpec[], minCount: number): boolean {
  if (workers.length < minCount) return false;
  return workers.every((worker) => validateNodeGroupResource(worker));
}

export function validateMasterNodesField(value: unknown, mode: MaasRunMode | undefined, t: TFunction): string | undefined {
  if (mode !== MaasRunMode.PrimarySecondary) {
    return undefined;
  }
  const masters = parseNodesField(value);
  if (!validateNodeGroupResource(masters[0])) {
    return t('data-center:maas.master.group.resource.required');
  }
  return undefined;
}

async function validateGpuUsageAcrossNodeFieldsLatest(
  masterValue: unknown,
  workerValue: unknown,
  mode: MaasRunMode | undefined,
  t: TFunction,
  gpuOptions?: GpuValidationOptions,
): Promise<string | undefined> {
  const violation = await findGpuUsageViolationFromNodeFieldsLatest(masterValue, workerValue, mode, gpuOptions);
  if (!violation) return undefined;
  return formatGpuUsageValidationError(t, violation);
}

export async function validateWorkerNodesFieldLatest(
  value: unknown,
  mode: MaasRunMode | undefined,
  t: TFunction,
  options?: {
    masterValue?: unknown;
    gpuOptions?: GpuValidationOptions;
  },
): Promise<string | undefined> {
  const workers = parseNodesField(value);
  if (mode !== MaasRunMode.Normal && mode !== MaasRunMode.PrimarySecondary) {
    return validateGpuUsageAcrossNodeFieldsLatest(options?.masterValue, value, mode, t, options?.gpuOptions);
  }
  if (mode === MaasRunMode.PrimarySecondary && workers.length < 1) {
    return t('data-center:maas.worker.group.at.least.one');
  }
  if (!validateWorkerGroups(workers, 1)) {
    return t('data-center:maas.worker.group.resource.required');
  }
  if (hasDuplicateWorkerGroupNames(workers)) {
    return t('data-center:maas.worker.group.name.duplicate');
  }
  if (mode === MaasRunMode.PrimarySecondary && hasWorkerGroupReservedRoleName(workers)) {
    return t('data-center:maas.worker.group.name.reserved');
  }
  return validateGpuUsageAcrossNodeFieldsLatest(options?.masterValue, value, mode, t, options?.gpuOptions);
}

/** 网关节点（单组）：勾选模块后须配置完整 CPU/内存等资源 */
export function validateGatewayNodesField(value: unknown, t: TFunction): string | undefined {
  const node = parseNodesField(value)[0] as EmbeddedNodeSpec | undefined;
  if (!node || !validateNodeGroupResource(node)) {
    return t('data-center:maas.gateway.group.resource.required');
  }
  return undefined;
}

export async function validateMasterNodesFieldWithGpuLatest(
  value: unknown,
  workerValue: unknown,
  mode: MaasRunMode | undefined,
  t: TFunction,
  gpuOptions?: GpuValidationOptions,
): Promise<string | undefined> {
  const baseError = validateMasterNodesField(value, mode, t);
  if (baseError) return baseError;
  return validateGpuUsageAcrossNodeFieldsLatest(value, workerValue, mode, t, gpuOptions);
}
